import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { StarRating, STATUS_STYLES, STATUS_LABEL, CATEGORIES } from "../lib/constants";
import { TrackingPanel } from "../components/TrackingPanel";
import { CompletionPhoto } from "../components/CompletionPhoto";
import { Itinerary } from "../components/Itinerary";
import { PaymentPanel, AddTipCard } from "../components/PaymentPanel";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { MapPin, Clock, ShieldCheck, CheckCircle2, Wallet, ArrowLeft, Loader2, Star, Apple, AlertTriangle, Lock, BellRing, Route } from "lucide-react";

export default function ErrandDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [errand, setErrand] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [myReviewDone, setMyReviewDone] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [panicOpen, setPanicOpen] = useState(false);

  const isRequester = errand && user && errand.requester_id === user.id;
  const isRunner = errand && user && errand.runner_id === user.id;
  const isParticipant = isRequester || isRunner;
  const cat = errand && (CATEGORIES.find((c) => c.value === errand.category)?.label || errand.category);

  const loadErrand = async () => {
    const { data } = await api.get(`/errands/${id}`);
    setErrand(data);
  };

  useEffect(() => { loadErrand(); /* eslint-disable-next-line */ }, [id]);

  const doAccept = async () => {
    setBusy(true);
    try {
      await api.post(`/errands/${id}/accept`);
      toast.success("You accepted this errand!");
      loadErrand();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/errands/${id}/status`, { status });
      toast.success(`Marked as ${STATUS_LABEL[status]}`);
      loadErrand();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/errands/${id}/cancel`, { origin_url: window.location.origin });
      if (data.fee_required && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setCancelOpen(false);
      if (data.stepped_down) {
        toast.success("You stepped down — the errand is open again for others.");
        navigate("/feed");
        return;
      }
      toast.success("Errand cancelled");
      loadErrand();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const raiseSafetyAlert = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/safety/alert", { errand_id: id });
      setPanicOpen(false);
      toast.success(`Alert sent to ${data.alerted} safety contact${data.alerted === 1 ? "" : "s"}.`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitReview = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", { errand_id: id, rating, comment: "" });
      toast.success("Review submitted!");
      setReviewOpen(false);
      setMyReviewDone(true);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!errand) return <div className="flex justify-center py-20 text-[#6B7280]"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-5 py-8">
      <button onClick={() => navigate(-1)} data-testid="detail-back-btn" className="flex items-center gap-1 text-sm font-semibold text-[#6B7280] hover:text-[#1A1D20] mb-5">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* main */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-3 space-y-6">
          {errand.status === "cancelled" && (
            <div data-testid="cancelled-banner" className="flex items-start gap-3 bg-[#FBF3F3] border border-[#F1D5D5] rounded-3xl p-5">
              <AlertTriangle className="text-[#B23B3B] mt-0.5 shrink-0" size={20} />
              <div>
                <p className="font-heading font-extrabold text-[#B23B3B]">This errand was cancelled</p>
                <p className="text-sm text-[#8A5151]">
                  {isRunner ? `${errand.requester_name} cancelled this errand. Thanks for offering to help!` : "This errand is no longer active."}
                </p>
              </div>
            </div>
          )}
          <div data-testid="errand-detail-card" className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6 sm:p-8">
            <div className="flex items-center justify-between gap-3 mb-4">
              <span className="text-xs font-bold uppercase tracking-wide text-secondary bg-secondary/10 px-3 py-1 rounded-full">{cat}</span>
              <span data-testid="errand-status-badge" className={`text-xs font-bold px-3 py-1 rounded-full ${STATUS_STYLES[errand.status]}`}>{STATUS_LABEL[errand.status]}</span>
            </div>
            <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-[#1A1D20] mb-3">{errand.title}</h1>
            <p className="text-[#4A5361] leading-relaxed mb-5 whitespace-pre-wrap">{errand.description}</p>
            <div className="flex flex-wrap gap-4 text-sm text-[#6B7280] mb-5">
              <span className="flex items-center gap-1.5">
                {errand.location_hidden ? <Lock size={14} className="text-[#6D4FC0]" /> : <MapPin size={15} />}
                {errand.location}{errand.location_hidden ? " (approx.)" : ""}
              </span>
              <span className="flex items-center gap-1.5"><Clock size={15} /> {errand.deadline}</span>
              {errand.is_multi_stop && (
                <span data-testid="detail-stops-badge" className="flex items-center gap-1.5 font-bold text-primary"><Route size={15} /> {errand.stop_count} stops</span>
              )}
            </div>
            {errand.location_hidden && (
              <div data-testid="location-locked-note" className="flex items-start gap-2 bg-[#EFEAFB] border border-[#DDD2F5] rounded-2xl p-3 text-sm text-[#5B3FB0] mb-5">
                <Lock size={15} className="mt-0.5 shrink-0" />
                <span>Exact location is hidden for safety. It's revealed only to the runner who accepts — and shared with their emergency contacts.</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-5 border-t border-gray-100">
              <div>
                <p className="text-xs text-[#9CA3AF] font-semibold uppercase">Budget</p>
                <p className="font-heading font-black text-3xl text-primary">${Number(errand.budget).toFixed(0)}</p>
              </div>
              {errand.paid && (
                <span data-testid="paid-badge" className="inline-flex items-center gap-1.5 bg-[#E7F6F0] text-[#2F8F6E] font-bold rounded-full px-4 py-2 text-sm">
                  <CheckCircle2 size={16} /> Paid
                </span>
              )}
            </div>
          </div>

          {/* Itinerary (multi-stop) */}
          {errand.is_multi_stop && (
            <Itinerary errand={errand} isRunner={isRunner} onChange={setErrand} />
          )}

          {/* Live tracking */}
          {isParticipant && ["accepted", "in_progress"].includes(errand.status) && (
            <TrackingPanel errandId={errand.id} isRunner={isRunner} />
          )}

          {/* Proof of completion */}
          {isParticipant && ["in_progress", "completed"].includes(errand.status) && (
            <CompletionPhoto errand={errand} isRunner={isRunner} isRequester={isRequester} onChange={loadErrand} />
          )}
        </motion.div>

        {/* sidebar */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="lg:col-span-2 space-y-6">
          {/* Requester */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6">
            <p className="text-xs text-[#9CA3AF] font-semibold uppercase mb-3">Posted by</p>
            <Link to={`/profile/${errand.requester_id}`} className="flex items-center gap-3 group">
              <span className="w-12 h-12 rounded-full bg-accent/20 text-accent flex items-center justify-center font-bold text-lg">
                {errand.requester_name?.[0]?.toUpperCase()}
              </span>
              <div>
                <p className="font-bold text-[#1A1D20] group-hover:text-primary transition-colors">{errand.requester_name}</p>
                <p className="text-xs text-[#6B7280]">View profile →</p>
              </div>
            </Link>
          </div>

          {errand.runner_name && (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6">
              <p className="text-xs text-[#9CA3AF] font-semibold uppercase mb-3">Runner</p>
              <Link to={`/profile/${errand.runner_id}`} className="flex items-center gap-3 group">
                <span className="w-12 h-12 rounded-full bg-secondary/20 text-secondary flex items-center justify-center font-bold text-lg">
                  {errand.runner_name?.[0]?.toUpperCase()}
                </span>
                <div>
                  <p className="font-bold text-[#1A1D20] group-hover:text-primary transition-colors">{errand.runner_name}</p>
                  <p className="text-xs text-[#6B7280]">View profile →</p>
                </div>
              </Link>
            </div>
          )}

          {/* Actions */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6 space-y-3">
            <p className="text-xs text-[#9CA3AF] font-semibold uppercase">Actions</p>

            {errand.status === "open" && !isRequester && (
              <Button data-testid="accept-errand-btn" onClick={doAccept} disabled={busy}
                className="w-full rounded-full h-12 font-bold bg-accent hover:opacity-90 active:scale-95 transition-transform">
                <ShieldCheck size={18} className="mr-2" /> Accept this errand
              </Button>
            )}
            {errand.status === "open" && isRequester && (
              <p className="text-sm text-[#6B7280] text-center py-2">Waiting for a runner to accept…</p>
            )}

            {isRunner && errand.status === "accepted" && (
              <Button data-testid="start-errand-btn" onClick={() => setStatus("in_progress")} disabled={busy}
                className="w-full rounded-full h-12 font-bold bg-secondary hover:opacity-90 active:scale-95 transition-transform">Start errand</Button>
            )}
            {isRunner && errand.status === "in_progress" && (
              <Button data-testid="complete-errand-btn" onClick={() => setStatus("completed")} disabled={busy}
                className="w-full rounded-full h-12 font-bold bg-accent hover:opacity-90 active:scale-95 transition-transform">
                <CheckCircle2 size={18} className="mr-2" /> Mark completed
              </Button>
            )}

            {isRunner && ["accepted", "in_progress"].includes(errand.status) && (
              <div data-testid="runner-escrow-note" className="flex items-start gap-2 bg-[#EEF6FF] border border-[#D6E7FA] rounded-2xl p-3 text-sm text-[#2C6FA0]">
                <Lock size={15} className="mt-0.5 shrink-0" />
                <span>You'll receive <strong>${Number(errand.budget).toFixed(0)}</strong> only after {errand.requester_name} confirms the errand is complete. Funds are held securely until then.</span>
              </div>
            )}
            {isRunner && ["accepted", "in_progress"].includes(errand.status) && (
              <Button data-testid="safety-alert-btn" onClick={() => setPanicOpen(true)}
                className="w-full rounded-full h-12 font-bold bg-[#B23B3B] hover:bg-[#9c3333] text-white active:scale-95 transition-transform animate-pulse">
                <BellRing size={18} className="mr-2" /> Safety Concern
              </Button>
            )}
            {(isRequester || isRunner) && errand.status === "completed" && !errand.paid && (
              <PaymentPanel errand={errand} isRequester={isRequester} isRunner={isRunner} onChange={setErrand} />
            )}
            {isRunner && errand.paid && (
              <div data-testid="runner-paid-note" className="flex items-center gap-2 bg-[#E7F6F0] border border-[#CDEBDF] rounded-2xl p-3 text-sm font-bold text-[#2F8F6E]">
                <CheckCircle2 size={16} /> Payment received · ${(Number(errand.budget) + Number(errand.tip || 0)).toFixed(2)}{errand.payment_method === "cash" ? " (cash)" : ""}
              </div>
            )}
            {isRequester && errand.paid && errand.payment_method === "card" && (
              <AddTipCard errand={errand} />
            )}
            {isRequester && errand.paid && errand.payment_method !== "card" && (
              <div data-testid="requester-paid-note" className="flex items-center gap-2 bg-[#E7F6F0] border border-[#CDEBDF] rounded-2xl p-3 text-sm font-bold text-[#2F8F6E]">
                <CheckCircle2 size={16} /> Paid ${(Number(errand.budget) + Number(errand.tip || 0)).toFixed(2)} (cash)
              </div>
            )}
            {isRequester && ["accepted", "in_progress"].includes(errand.status) && (
              <div data-testid="requester-escrow-note" className="flex items-start gap-2 bg-[#EEF6FF] border border-[#D6E7FA] rounded-2xl p-3 text-sm text-[#2C6FA0]">
                <Lock size={15} className="mt-0.5 shrink-0" />
                <span>Your runner is paid <strong>${Number(errand.budget).toFixed(0)}</strong> only after you mark the errand complete — you're always in control.</span>
              </div>
            )}

            {isParticipant && errand.status === "completed" && !myReviewDone && (
              <Button data-testid="leave-review-btn" onClick={() => setReviewOpen(true)} variant="outline"
                className="w-full rounded-full h-12 font-bold border-2">
                <Star size={16} className="mr-2" /> Leave a review
              </Button>
            )}

            {isParticipant && ["open", "accepted", "in_progress"].includes(errand.status) && (
              <Button data-testid="cancel-errand-btn" onClick={() => setCancelOpen(true)} disabled={busy} variant="ghost"
                className="w-full rounded-full h-11 font-semibold text-[#B23B3B] hover:bg-[#FBE9E9]">Cancel Errand</Button>
            )}

            {!isParticipant && errand.status !== "open" && (
              <p className="text-sm text-[#6B7280] text-center py-2">This errand is {STATUS_LABEL[errand.status].toLowerCase()}.</p>
            )}
          </div>

          <AlertDialog open={panicOpen} onOpenChange={setPanicOpen}>
            <AlertDialogContent className="rounded-3xl" data-testid="safety-alert-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading flex items-center gap-2 text-[#B23B3B]">
                  <BellRing size={20} /> Alert your safety contacts?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This immediately notifies all of your emergency contacts that you have a safety concern during "{errand.title}", along with this errand's details. Use this if you feel unsafe.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="safety-alert-no" className="rounded-full font-semibold">Never mind</AlertDialogCancel>
                <AlertDialogAction data-testid="safety-alert-yes" onClick={(ev) => { ev.preventDefault(); raiseSafetyAlert(); }} disabled={busy}
                  className="rounded-full font-bold bg-[#B23B3B] hover:bg-[#9c3333]">
                  {busy ? <Loader2 className="animate-spin" size={16} /> : "Send alert now"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
            <AlertDialogContent className="rounded-3xl" data-testid="detail-cancel-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading">
                  {isRunner && errand.status !== "open" ? "Step down from this errand?" : "Cancel this errand?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {isRunner && errand.status !== "open"
                    ? `You'll be unassigned and "${errand.title}" will reopen for another neighbor. No fee applies.`
                    : `"${errand.title}" will be cancelled.${errand.status !== "open" ? ` ${errand.runner_name || "The runner"} will be notified.` : ""} This can't be undone.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              {isRequester && errand.status !== "open" && (
                <div data-testid="detail-cancel-fee-notice" className="flex items-start gap-2 bg-[#FFF4EE] border border-[#FFD9C7] rounded-2xl p-3 text-sm text-[#8A3D1E]">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>A <strong>$7 cancellation fee</strong> applies because a runner already accepted. You'll be taken to secure checkout.</span>
                </div>
              )}
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="detail-cancel-no" className="rounded-full font-semibold">
                  {isRunner && errand.status !== "open" ? "Stay on errand" : "Keep errand"}
                </AlertDialogCancel>
                <AlertDialogAction data-testid="detail-cancel-yes" onClick={(ev) => { ev.preventDefault(); doCancel(); }} disabled={busy}
                  className="rounded-full font-bold bg-[#B23B3B] hover:bg-[#9c3333]">
                  {busy ? <Loader2 className="animate-spin" size={16} />
                    : isRequester && errand.status !== "open" ? "Pay $7 & cancel"
                    : isRunner && errand.status !== "open" ? "Step down"
                    : "Yes, cancel it"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {reviewOpen && (
            <div data-testid="review-form" className="bg-white rounded-3xl border-2 border-primary/30 shadow-soft p-6 space-y-4">
              <p className="font-heading font-extrabold text-[#1A1D20]">Rate your experience</p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button key={i} data-testid={`review-star-${i}`} onClick={() => setRating(i)} type="button">
                    <Star size={30} className={i <= rating ? "fill-[#FFB020] text-[#FFB020]" : "text-gray-300"} />
                  </button>
                ))}
              </div>
              <Button data-testid="submit-review-btn" onClick={submitReview} disabled={busy}
                className="w-full rounded-full h-11 font-bold bg-primary hover:bg-[#FF8D70]">Submit review</Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
